// src/features/project/ItemDetailRail.tsx
// Right-rail detail for a single ProjectItem. Opening an item must not throw away
// the board/backlog, so this is a rail, not a page. Refreshes the item via GetItem
// on open and wires the item-level commands: UpdateItem, MoveItem, RemoveItem, and
// (un)assign to sprint / epic / component.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n, useProjectItem, useProjectSprint, useProjectEpic, useProjectComponent } from '@react/hooks';
import { Button, Input, Badge } from '@react/components';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Field } from './ProjectModal';
import { entityRef, idOf, listParams } from './meta';
import { getDisplayName } from './logic';
import type { Entity } from '@core/types/entity';
import type { ProjectItem, BoardColumn, Sprint, Epic, ProjectComponent } from '@core/types/project';

interface ItemDetailRailProps {
  projectEntity: Entity;
  item: ProjectItem;
  columns: BoardColumn[];
  onClose: () => void;
  onChanged: () => void;
}

export const ItemDetailRail = observer(
  ({ projectEntity, item, columns, onClose, onChanged }: ItemDetailRailProps) => {
    const { t } = useI18n();
    const itemStore = useProjectItem();
    const sprintStore = useProjectSprint();
    const epicStore = useProjectEpic();
    const componentStore = useProjectComponent();

    const projectId = projectEntity.id;
    const itemId = idOf(item);

    const [live, setLive] = useState<ProjectItem>(item);
    const [sprints, setSprints] = useState<Sprint[]>([]);
    const [epics, setEpics] = useState<Epic[]>([]);
    const [components, setComponents] = useState<ProjectComponent[]>([]);
    const [points, setPoints] = useState<string>(item.story_points != null ? String(item.story_points) : '');
    const [size, setSize] = useState<string>(item.t_shirt_size ?? '');
    const [busy, setBusy] = useState(false);
    const [confirmRemove, setConfirmRemove] = useState(false);

    // Refresh the item on open — the list row may be stale.
    useEffect(() => {
      let alive = true;
      setLive(item);
      setPoints(item.story_points != null ? String(item.story_points) : '');
      setSize(item.t_shirt_size ?? '');
      itemStore
        .GetItem(projectId, itemId)
        .then((full) => {
          if (alive && full && full.entity) {
            setLive(full);
            setPoints(full.story_points != null ? String(full.story_points) : '');
            setSize(full.t_shirt_size ?? '');
          }
        })
        .catch(() => {});
      return () => {
        alive = false;
      };
    }, [itemId]);

    useEffect(() => {
      let alive = true;
      sprintStore.ListSprints(projectId, listParams()).then(() => alive && setSprints([...sprintStore.items]));
      epicStore.ListEpics(projectId, listParams()).then(() => alive && setEpics([...epicStore.items]));
      componentStore
        .ListComponents(projectId, listParams())
        .then(() => alive && setComponents([...componentStore.items]));
      return () => {
        alive = false;
      };
    }, [projectId]);

    const run = async (fn: () => Promise<unknown>) => {
      setBusy(true);
      try {
        await fn();
        onChanged();
      } catch {
        /* store surfaces the error */
      } finally {
        setBusy(false);
      }
    };

    const saveMetadata = () =>
      run(() =>
        itemStore.UpdateItem(projectId, itemId, {
          item: {
            ...live,
            story_points: points === '' ? undefined : Number(points),
            t_shirt_size: size,
          },
          field_mask: { paths: ['story_points', 't_shirt_size'] },
        } as never),
      );

    const moveColumn = (columnId: string) =>
      run(() =>
        itemStore.MoveItem(projectId, itemId, {
          project: projectEntity,
          item: live.entity!,
          to_column_id: columnId,
          position: live.position ?? 0,
        } as never),
      );

    const assignSprint = (sprintId: string) => {
      const currentId = live.sprint?.id;
      if (sprintId === (currentId ?? '')) return;
      run(async () => {
        if (currentId) await sprintStore.RemoveItemFromSprint(projectId, currentId, itemId);
        if (sprintId)
          await sprintStore.AddItemToSprint(projectId, sprintId, {
            project: projectEntity,
            sprint: entityRef(sprintId, 'sprint', projectEntity),
            item: live.entity!,
          } as never);
      });
    };

    const assignEpic = (epicId: string) => {
      const currentId = live.epic?.id;
      if (epicId === (currentId ?? '')) return;
      run(async () => {
        if (currentId) await epicStore.RemoveItemFromEpic(projectId, currentId, itemId);
        if (epicId)
          await epicStore.AddItemToEpic(projectId, epicId, {
            project: projectEntity,
            epic: entityRef(epicId, 'epic', projectEntity),
            item: live.entity!,
          } as never);
      });
    };

    const currentComponentIds = new Set((live.components ?? []).map((c) => c.id));
    const toggleComponent = (componentId: string) => {
      run(async () => {
        if (currentComponentIds.has(componentId)) {
          await componentStore.RemoveItemFromComponent(projectId, componentId, itemId);
        } else {
          await componentStore.AddItemToComponent(projectId, componentId, {
            project: projectEntity,
            component: entityRef(componentId, 'project_component', projectEntity),
            item: live.entity!,
          } as never);
        }
      });
    };

    const remove = () =>
      run(async () => {
        await itemStore.RemoveItem(projectId, itemId);
        setConfirmRemove(false);
        onClose();
      });

    return (
      <aside
        className="flex h-full w-full flex-col border-l border-rail-border bg-rail text-rail-foreground"
        aria-label={t('project.item.detail')}
      >
        <header className="flex items-start justify-between gap-2 border-b border-rail-border px-4 py-3">
          <div className="min-w-0">
            {live.item_key && (
              <span className="font-mono text-xs text-muted-foreground">{live.item_key}</span>
            )}
            <h2 className="truncate text-sm font-semibold text-foreground">
              {live.title || t('common.untitled')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {live.status && <Badge variant="secondary" size="sm">{live.status}</Badge>}
            {live.assignee && <span>{getDisplayName(live.assignee)}</span>}
          </div>

          {/* Move column */}
          {columns.length > 0 && (
            <Field label={t('project.item.column')} htmlFor="rail-column">
              <Select
                id="rail-column"
                value={live.column_id ?? ''}
                disabled={busy}
                onChange={(e) => moveColumn(e.target.value)}
                aria-label={t('project.item.column')}
              >
                <option value="">{t('project.item.noColumn')}</option>
                {columns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          {/* Sprint */}
          <Field label={t('project.item.sprint')} htmlFor="rail-sprint">
            <Select
              id="rail-sprint"
              value={live.sprint?.id ?? ''}
              disabled={busy}
              onChange={(e) => assignSprint(e.target.value)}
              aria-label={t('project.item.sprint')}
            >
              <option value="">{t('project.item.backlogOption')}</option>
              {sprints.map((s) => (
                <option key={idOf(s)} value={idOf(s)}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>

          {/* Epic */}
          <Field label={t('project.item.epic')} htmlFor="rail-epic">
            <Select
              id="rail-epic"
              value={live.epic?.id ?? ''}
              disabled={busy}
              onChange={(e) => assignEpic(e.target.value)}
              aria-label={t('project.item.epic')}
            >
              <option value="">{t('project.item.noEpic')}</option>
              {epics.map((ep) => (
                <option key={idOf(ep)} value={idOf(ep)}>
                  {ep.name}
                </option>
              ))}
            </Select>
          </Field>

          {/* Components */}
          {components.length > 0 && (
            <Field label={t('project.item.components')}>
              <div className="flex flex-wrap gap-1.5">
                {components.map((c) => {
                  const on = currentComponentIds.has(idOf(c));
                  return (
                    <button
                      key={idOf(c)}
                      type="button"
                      disabled={busy}
                      onClick={() => toggleComponent(idOf(c))}
                      aria-pressed={on}
                      className={`rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                        on
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}

          {/* Estimation */}
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('project.item.storyPoints')} htmlFor="rail-points">
              <Input
                id="rail-points"
                type="number"
                inputMode="numeric"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                placeholder="—"
              />
            </Field>
            <Field label={t('project.item.size')} htmlFor="rail-size">
              <Input id="rail-size" value={size} onChange={(e) => setSize(e.target.value)} placeholder="—" />
            </Field>
          </div>
          <Button size="sm" variant="secondary" onClick={saveMetadata} disabled={busy}>
            {t('project.item.saveMetadata')}
          </Button>

          {/* Labels (read-only display) */}
          {(live.labels?.length ?? 0) > 0 && (
            <Field label={t('project.item.labels')}>
              <div className="flex flex-wrap gap-1.5">
                {live.labels.map((l) => (
                  <span
                    key={idOf(l) || l.name}
                    className="rounded-full px-2 py-0.5 text-xs text-white"
                    style={{ backgroundColor: l.color || undefined }}
                  >
                    {l.name}
                  </span>
                ))}
              </div>
            </Field>
          )}
        </div>

        <footer className="border-t border-rail-border px-4 py-3">
          <Button size="sm" variant="danger" onClick={() => setConfirmRemove(true)} disabled={busy}>
            {t('project.item.remove')}
          </Button>
        </footer>

        <ConfirmDialog
          open={confirmRemove}
          title={t('project.item.removeConfirmTitle')}
          description={t('project.item.removeConfirmDesc')}
          destructive
          isConfirming={busy}
          onConfirm={remove}
          onCancel={() => setConfirmRemove(false)}
        />
      </aside>
    );
  },
);

ItemDetailRail.displayName = 'ItemDetailRail';
